import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'anf-core-react';
import { usePhoneNumberValidation } from '../../hooks';

/**
 * PaymentSupportMessage - Displays support message when WSS payment update is not available
 * Automatically converts phone numbers to clickable links for mobile users
 * Matches the existing WSS flow styling for disabled payment update scenarios
 */
function PaymentSupportMessage({ message }) {
  const validate = usePhoneNumberValidation();

  if (!message || !message.value) {
    return null;
  }

  const parseMessageWithPhoneLinks = (text) => {
    // Splits text to get potential phone number segments
    // Lets usePhoneNumberValidation do the validation
    const segments = text.split(/(\s+)/);
    const parts = [];

    segments.forEach((segment, index) => {
      if (!segment.trim()) {
        // Handles whitespace segments
        if (parts.length > 0 && parts[parts.length - 1].type === 'text') {
          parts[parts.length - 1].content += segment;
        } else {
          parts.push({
            type: 'text',
            content: segment,
            key: `text-${index}`,
          });
        }
        return;
      }

      const { isValid, formattedPhoneNumber } = validate(segment.trim());

      if (isValid) {
        // Uses the formatted phone number from the validation hook for the tel: link
        // Removes any formatting except + and digits
        const cleanPhone = formattedPhoneNumber.replace(/[^\d+]/g, '');
        const telHref = `tel:${cleanPhone}`;

        // Add phone number as a link using the existing Link component pattern
        parts.push({
          type: 'phone',
          content: segment,
          href: telHref,
          key: `phone-${index}`,
        });
        return;
      }

      // Adds as text
      if (parts.length > 0 && parts[parts.length - 1].type === 'text') {
        parts[parts.length - 1].content += segment;
      } else {
        parts.push({
          type: 'text',
          content: segment,
          key: `text-${index}`,
        });
      }
    });

    return parts.length > 0 ? parts : [{
      type: 'text',
      content: text,
      key: 'text-full',
    }];
  };

  const messageElements = parseMessageWithPhoneLinks(message.value);

  return (
    <div className="payment-support-message">
      <div>
        {messageElements.map((element) => (
          element.type === 'text' ? (
            <span key={element.key}>{element.content}</span>
          ) : (
            <Link key={element.key} classList="link-phoneNumber" href={element.href}>
              {element.content}
            </Link>
          )
        ))}
      </div>
    </div>
  );
}

PaymentSupportMessage.propTypes = {
  message: PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.string,
  }),
};

PaymentSupportMessage.defaultProps = {
  message: null,
};

export default PaymentSupportMessage;
